import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUpIcon, ShieldCheckIcon, ActivityIcon, ArrowRightIcon } from 'lucide-react';
interface PatternRecognitionProps {
  answers: Record<number, string>;
  onContinue: () => void;
}
const traits = [{
  icon: TrendingUpIcon,
  label: 'Outcome-driven',
  description: 'You prioritize long-term patient outcomes over short-term convenience',
  color: 'teal',
  strength: 92
}, {
  icon: ShieldCheckIcon,
  label: 'Risk-aware',
  description: 'You carefully weigh safety profiles and evidence strength',
  color: 'indigo',
  strength: 78
}, {
  icon: ActivityIcon,
  label: 'Conservative escalation',
  description: 'You prefer established therapies with robust data before adopting new options',
  color: 'amber',
  strength: 65
}];
const colorMap: Record<string, {
  bg: string;
  text: string;
  bar: string;
}> = {
  teal: {
    bg: 'bg-teal-50',
    text: 'text-teal-600',
    bar: 'bg-teal-500'
  },
  indigo: {
    bg: 'bg-indigo-50',
    text: 'text-indigo-600',
    bar: 'bg-indigo-500'
  },
  amber: {
    bg: 'bg-amber-50',
    text: 'text-amber-600',
    bar: 'bg-amber-500'
  }
};
export function PatternRecognition({
  answers,
  onContinue
}: PatternRecognitionProps) {
  return <div className="w-full min-h-screen flex items-center justify-center bg-[#FAFAF8] px-6 py-20" data-id="element-122">
      <div className="max-w-xl w-full space-y-8" data-id="element-123">
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="space-y-2" data-id="element-124">
          <span className="inline-block px-3 py-1 bg-indigo-100 text-indigo-700 text-xs font-bold rounded-full uppercase tracking-wide" data-id="element-125">
            Pattern detected
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-[#1C1917] leading-snug" data-id="element-126">
            Your answers reveal a pattern
          </h2>
          <p className="text-[#57534E]" data-id="element-127">
            3 traits emerged from your decision-making style
          </p>
        </motion.div>

        <div className="space-y-4" data-id="element-128">
          {traits.map((trait, index) => {
          const Icon = trait.icon;
          const colors = colorMap[trait.color];
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: 0.1 * index
          }} className="bg-white rounded-2xl p-5 shadow-sm border border-neutral-100" data-id="element-129">
                <div className="flex items-start gap-4" data-id="element-130">
                  <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center`} data-id="element-131">
                    <Icon className={`w-5 h-5 ${colors.text}`} data-id="element-132" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-133">
                    <div className="flex items-baseline justify-between gap-2 mb-1" data-id="element-134">
                      <h3 className="text-base font-bold text-[#1C1917]" data-id="element-135">
                        {trait.label}
                      </h3>
                      <span className={`text-sm font-bold ${colors.text}`} data-id="element-136">
                        {trait.strength}%
                      </span>
                    </div>
                    <p className="text-sm text-[#57534E] mb-3" data-id="element-137">
                      {trait.description}
                    </p>
                    <div className="h-1.5 w-full bg-neutral-100 rounded-full overflow-hidden" data-id="element-138">
                      <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: `${trait.strength}%`
                  }} transition={{
                    duration: 0.8,
                    delay: 0.3 + 0.15 * index,
                    ease: 'easeOut'
                  }} className={`h-full rounded-full ${colors.bar}`} data-id="element-139" />
                    </div>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.8
      }} className="flex justify-center pt-4 pb-20" data-id="element-140">
          <button onClick={onContinue} className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#1C1917] text-white font-bold rounded-xl hover:bg-neutral-800 transition-all shadow-md hover:-translate-y-0.5" data-id="element-141">
            Reveal your segment
            <ArrowRightIcon className="w-5 h-5" data-id="element-142" />
          </button>
        </motion.div>
      </div>
    </div>;
}